import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Tag, Spin, message, Modal } from 'antd';
import { Trash2 } from "lucide-react";
import { PlusOutlined } from '@ant-design/icons';
import ModalWrapper from '../../../../Modal';
import RequestOverTime from '../../../../modals/requestOverTime';
import { getRequest, deleteRequest } from '../../../../service';
import { getEmployeeId} from '../../../../utils/localStorage';
import { formatDate } from '../../../../utils/dateTime';

const MyOvertime = () => {

    const [editShiftModalOpen, setEditShiftModalOpen] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [loading, setLoading] = useState(false);
    const [overtimeRequests, setOvertimeRequests] = useState([]);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [recordToDelete, setRecordToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

 

    // Fetch overtime requests from API
    const fetchOvertimeRequests = async () => {
        const employeeId = getEmployeeId();
        if (!employeeId) {
            message.error('Employee ID not found');
            return;
        }

        setLoading(true);
        try {
            const response = await getRequest(`/api/overtimerequest?employeeId=${employeeId}&onlyMe=true`);
            if (response.overtimeRequests) {
                setOvertimeRequests(response.overtimeRequests);
            } else {
                setOvertimeRequests([]);
            }
        } catch (error) {
            console.error('Error fetching overtime requests:', error);
            message.error('Failed to fetch overtime requests');
        } finally {
            setLoading(false);
        }
    };

    // Load data on component mount
    useEffect(() => {
        fetchOvertimeRequests();
    }, []);

    const getStatusColor = (status) => {
        switch (status.toLowerCase()) {
            case 'approved':
                return 'success';
            case 'pending':
                return 'warning';
            case 'rejected':
                return 'error';
            default:
                return 'default';
        }
    };

    // Format time for display
    const formatTime = (timeString) => {
        if (!timeString) return '-';
        return timeString;
    };

    // Handler to open the modal for requesting overtime
    const handleRequestOvertimeClick = () => {
        setSelectedEntry(null); // Clear any previous entry
        setEditShiftModalOpen(true);
    };

    // Handler to close the modal
    const handleCloseEditShiftModal = () => {
        setEditShiftModalOpen(false);
    };

    // Handler for successful edit/request
    const handleEditSuccess = () => {
        setEditShiftModalOpen(false);
        // Refresh the data after successful request
        fetchOvertimeRequests();
    };

    // Handler to show delete confirmation modal
    const handleDeleteClick = (record) => {
        setRecordToDelete(record);
        setDeleteModalVisible(true);
    };

    // Handler to confirm delete
    const handleConfirmDelete = async () => {
        if (!recordToDelete) return;

        setDeleteLoading(true);
        try {
            await deleteRequest(`/api/overtimerequest?id=${recordToDelete._id}`);
            message.success('Overtime request deleted successfully');
            setDeleteModalVisible(false);
            setRecordToDelete(null);
            // Refresh the data after successful deletion
            fetchOvertimeRequests();
        } catch (error) {
            console.error('Error deleting overtime request:', error);
            message.error('Failed to delete overtime request');
        } finally {
            setDeleteLoading(false);
        }
    };

    // Handler to cancel delete
    const handleCancelDelete = () => {
        setDeleteModalVisible(false);
        setRecordToDelete(null);
    };

    const columns = [
        {
            title: 'Date',
            dataIndex: 'attendanceLog',
            key: 'date',
            render: (attendanceLog) => formatDate(attendanceLog?.date),
        },
        {
            title: 'Check In',
            dataIndex: 'attendanceLog',
            key: 'checkIn',
            render: (attendanceLog) => formatTime(attendanceLog?.checkIn),
        },
        {
            title: 'Check Out',
            dataIndex: 'attendanceLog',
            key: 'checkOut',
            render: (attendanceLog) => formatTime(attendanceLog?.checkOut),
        },
        {
            title: 'Total Overtime',
            dataIndex: 'totalOverTime',
            key: 'totalOverTime',
            render: (totalOverTime) => totalOverTime || '-',
        },
        {
            title: 'Paid Overtime',
            dataIndex: 'paidOverTime',
            key: 'paidOverTime',
            render: (paidOverTime) => paidOverTime || '-',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={getStatusColor(status)}>
                    {status?.charAt(0).toUpperCase() + status?.slice(1)}
                </Tag>
            ),
        },
        {
            title: 'Approved By',
            dataIndex: 'approvedBy',
            key: 'approvedBy',
            render: (approvedBy) => {
                if (!approvedBy) return '-';
                return `${approvedBy.firstName || ''} ${approvedBy.lastName || ''}`.trim() || '-';
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button 
                        type="text" 
                        icon={<Trash2 className={`w-4 h-4 ${record.status?.toLowerCase() === 'pending' ? 'text-red-600' : 'text-gray-400'}`} />}
                        onClick={() => handleDeleteClick(record)}
                        disabled={record.status?.toLowerCase() !== 'pending'}
                        title={record.status?.toLowerCase() !== 'pending' ? 'Cannot delete approved/rejected requests' : 'Delete overtime request'}
                    />
                </Space>
            ),
        },
    ];

    return (
        <>
            <div className="p-4">
                <div className="flex justify-between items-center mb-4">
                    <h1 className="text-2xl font-semibold">My Overtime Requests</h1>
                    <Button
                        className='bg-primary'
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleRequestOvertimeClick}
                    >
                        Request Overtime
                    </Button>
                </div>

                <Spin spinning={loading}>
                    <Table
                        columns={columns}
                        dataSource={overtimeRequests}
                        rowKey="_id"
                        scroll={{ x: 'max-content' }}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                        }}
                        locale={{
                            emptyText: loading ? 'Loading...' : 'No overtime requests found'
                        }}
                    />
                </Spin>
            </div>
            <ModalWrapper
                open={editShiftModalOpen}
                onRequestClose={handleCloseEditShiftModal}
                title="Request Overtime"
            >
                <RequestOverTime
                    onClose={handleCloseEditShiftModal}
                    onSuccess={handleEditSuccess}
                    initialData={selectedEntry || {}}
                />
            </ModalWrapper>

            {/* Delete Confirmation Modal */}
            <Modal
                title="Confirm Delete"
                open={deleteModalVisible}
                onOk={handleConfirmDelete}
                onCancel={handleCancelDelete}
                confirmLoading={deleteLoading}
                okText="Delete"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
            >
                <p>Are you sure you want to delete this overtime request?</p>
                <p>This action cannot be undone.</p>
            </Modal>
        </>
    );
}

export default MyOvertime;
